import { useCallback, useEffect, useRef, useState } from 'react';

import { Platform } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { format } from 'date-fns';
// eslint-disable-next-line import/no-extraneous-dependencies
import isEqual from 'lodash/isEqual';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { setUnlockId } from 'store/details/actions';
import { selectUnlockedId } from 'store/details/selectors';
import { setShouldUpdateId } from 'store/player/actions';
import { StreamTypes } from 'store/player/actions.types';
import { selectPlayerUpdateId } from 'store/player/selectors';
import { selectChannelsById } from 'store/programs/selectors';
import { RBChannel } from 'store/railsBuilder/reducer.types';
import { setVideoSessionId } from 'store/session/actions';
import { setCashedPin, setOTTLastConnection } from 'store/user/actions';
import {
    selectActiveUserProfile,
    selectCashedPins,
    selectEntitlements,
} from 'store/user/selectors';

import {
    EventParentalControlRating,
    VideoItem,
} from 'services/data-manager/types';
import { RegularStorage, StorageKeys } from 'services/mmkv';

import { ERROR_CODE_A201 } from 'utils/error/errorCodes';
import useProfileParentalRating from 'utils/hooks/useProfileParentalRating';
import isSubscribedToChannel from 'utils/isSubscribedToChannel';
import { isEmpty } from 'utils/object';
import { lockToDefaultOrientation } from 'utils/orientation';
import needsToLockContent from 'utils/parentalControl/needsToLockContent';
import needsToShowPinDialog from 'utils/parentalControl/needsToShowPinDialog';
import { getParentalControlRatingNumbers } from 'utils/parentalControl/parentalControlRatingNumbers';
import { isPhone, isSafari } from 'utils/responsiveness';
import { getUniqueId } from 'utils/uniqueId';

import { BitmovinPlayerSource } from 'components/Player/types.ts';

import { PlayerError } from '../types';
import { onAfterHidePlayerModal, onBeforeShowPlayerModal } from '../utils';
import useContentRatingOverlay from './useContentRatingOverlay';
import useGetDrmContentToken from './useGetDrmContentToken';
import useParentalControl from './useParentalControl';
import usePlayerBlackout from './usePlayerBlackout';
import useResetPrevChannelOnPlayerExit from './useResetPrevChannelOnPlayerExit';
import useVideoItem from './useVideoItem';

type Params = {
    fullscreen: boolean;
    embedded: boolean;
    outOfHomeBlockStream: boolean;
    originalVideoItem: VideoItem | undefined;
    originalPlaybackType: StreamTypes | undefined;
    channel: RBChannel | undefined;
    onSetPlayerError(error: PlayerError, videoSessionId: string): void;
    onUnsubscribedChannelError(videoSessionId: string): void;
    onResetPlayerError(): void;
    visible: boolean;
    shouldWarnOnMobileData?: boolean;
};

const {
    PLAYER_LOCKED_CONTENT_OVERLAY_TITLE,
    PLAYER_LOCKED_CONTENT_OVERLAY_DESCRIPTION_RN,
} = LANGUAGE_KEYS;

function useVideoSource({
    fullscreen,
    embedded,
    visible,
    originalVideoItem,
    originalPlaybackType,
    channel,
    outOfHomeBlockStream,
    onSetPlayerError,
    onUnsubscribedChannelError,
    onResetPlayerError,
    shouldWarnOnMobileData,
}: Params) {
    const dispatch = useDispatch();

    const entitlements = useSelector(selectEntitlements);
    const channelsById = useSelector(selectChannelsById);
    const profileParentalControlRating = useProfileParentalRating();
    const shouldUpdateId = useSelector(selectPlayerUpdateId);
    const unlockedDetailId = useSelector(selectUnlockedId);
    const cashedPins = useSelector(selectCashedPins);
    const selectedProfileId = useSelector(selectActiveUserProfile);

    const onGetDrmContentToken = useGetDrmContentToken();
    const { videoItem, playbackType, onResetVideoItem } = useVideoItem(
        originalVideoItem,
        originalPlaybackType,
    );
    const { isPlayerBlackout } = usePlayerBlackout({
        videoItem,
        channel,
        playbackType,
    });
    const { t } = useTranslation();

    const videoSessionId = useRef<string>('');
    const prevVideoSessionId = useRef<string>('');
    const prevVideoItem = useRef<VideoItem | undefined>(videoItem);
    const prevPlaybackType = useRef(playbackType);
    const prevOutOfHomeBlockStream = useRef<boolean>(outOfHomeBlockStream);
    const prevParentalRating = useRef(0);
    const prevParentalRatingChannelId = useRef(videoItem?.channelId);
    const cashedTimestampsByRating = useRef<string | null | undefined>(
        undefined,
    );

    const [videoSource, setVideoSource] = useState<BitmovinPlayerSource>();

    useResetPrevChannelOnPlayerExit({ prevParentalRatingChannelId, visible });

    const {
        showPinDialog,
        onShowPinDialog,
        onParentalCheckDismiss,
        onParentalControlChecked,
    } = useParentalControl({
        fullscreen,
        onSetPlayerError,
        onResetPlayerError,
    });

    const onStopPlaybackSession = useCallback(() => {
        prevVideoSessionId.current = videoSessionId.current;
        dispatch(
            setOTTLastConnection(format(new Date(), 'yyyy-MM-dd HH:mm:ss')),
        );
    }, [prevVideoSessionId, videoSessionId]);

    const handleResetVideoSource = useCallback(
        (resetSource = true) => {
            onStopPlaybackSession();

            if (resetSource) {
                setVideoSource(undefined);
            }

            dispatch(setUnlockId(undefined));
        },
        [onStopPlaybackSession],
    );

    const handleSetContentTokenError = useCallback(
        (error: any) => {
            onSetPlayerError(error as PlayerError, videoSessionId.current!);
            onStopPlaybackSession();
        },
        [onSetPlayerError, onStopPlaybackSession],
    );

    const handleCreateStreamSession = useCallback(() => {
        try {
            const streamUrl = videoItem!.playbackUrls?.find(
                (playback) => playback.type === playbackType,
            )?.url;

            if (!streamUrl) {
                // eslint-disable-next-line no-throw-literal
                throw {
                    message: 'No relevant playback url found.',
                } as PlayerError;
            }

            if (!videoItem?.isFreeToAir && videoItem?.drmId) {
                if (
                    !(
                        playbackType === StreamTypes.live &&
                        streamUrl === videoSource?.src
                    )
                ) {
                    // [REAC-650]: In case of Android, Nagra Player "src" property update gives
                    // stopping the player, and then restarting playback again, which is not
                    // happening on IOS and Web, so, only for android we need to check if
                    // stream url is not changed in live playback - then no need of updating
                    // player "src" property to avoid black screen flickering between player stop and
                    // restart actions.
                    setVideoSource({
                        src: streamUrl,
                        type: Platform.select({
                            ios: 'hls',
                            android: 'dash',
                            web: isSafari ? 'hls' : 'dash',
                            default: 'none',
                        }),
                        sourceConfig: videoItem.sourceConfig,
                        onGetContentToken: () =>
                            onGetDrmContentToken(videoItem.drmId!),
                    });
                }
            } else {
                setVideoSource((prevVideoSource) => {
                    if (
                        playbackType === StreamTypes.live &&
                        streamUrl === prevVideoSource?.src
                    ) {
                        return prevVideoSource;
                    }

                    return {
                        src: streamUrl,
                        type: Platform.select({
                            ios: 'hls',
                            android: 'dash',
                            web: isSafari ? 'hls' : 'dash',
                            default: 'none',
                        }),
                        sourceConfig: videoItem?.sourceConfig,
                    };
                });
            }
        } catch (error: any) {
            handleSetContentTokenError(error);
        }
    }, [
        videoItem,
        playbackType,
        videoSource?.src,
        onGetDrmContentToken,
        handleSetContentTokenError,
    ]);

    const { showContentRatingOverlay, onShowContentRatingOverlay } =
        useContentRatingOverlay({
            handleCreateStreamSession,
        });

    const checkCashedPinByRating = useCallback(
        (eventRating?: EventParentalControlRating) => {
            if (
                eventRating &&
                cashedPins &&
                !isEmpty(cashedPins) &&
                selectedProfileId &&
                cashedPins[selectedProfileId]
            ) {
                const storedParentalRatings = Object.keys(
                    cashedPins[selectedProfileId],
                );

                return (
                    Date.now() -
                        (cashedPins[selectedProfileId][
                            storedParentalRatings[
                                storedParentalRatings.length - 1
                            ] as unknown as EventParentalControlRating
                        ] || 0) >
                    86400000
                );
            }

            return true;
        },
        [cashedPins, selectedProfileId],
    );

    useEffect(() => {
        const baseRequirement = videoItem && !outOfHomeBlockStream;

        if (
            (baseRequirement && !isEqual(videoItem, prevVideoItem.current)) ||
            (baseRequirement &&
                playbackType &&
                playbackType !== prevPlaybackType.current) ||
            (baseRequirement &&
                outOfHomeBlockStream !== prevOutOfHomeBlockStream.current)
        ) {
            onStopPlaybackSession();

            if (shouldUpdateId) {
                videoSessionId.current = getUniqueId();
            } else {
                videoSessionId.current = prevVideoSessionId.current;
            }

            dispatch(setVideoSessionId(videoSessionId.current));
            dispatch(setShouldUpdateId(true));

            onResetPlayerError();

            const prevChannelId = prevVideoItem.current?.channelId;
            const prevProgramId = prevVideoItem.current?.id;
            const prevProgramStart = prevVideoItem.current?.startTime;
            const newProgramIsAboutToPlay =
                videoItem &&
                (prevProgramId !== videoItem.id ||
                    prevProgramStart !== videoItem.startTime);
            const newChannelIsAboutToPlay =
                videoItem && prevChannelId !== videoItem.channelId;
            const programOrChannelChanged =
                newProgramIsAboutToPlay || newChannelIsAboutToPlay;
            const networkChanged =
                outOfHomeBlockStream !== prevOutOfHomeBlockStream.current;

            // Channel related checks
            const channelSource =
                videoItem.channelId && channelsById[videoItem.channelId];
            const notSubscribed =
                programOrChannelChanged &&
                playbackType !== StreamTypes.vod &&
                (!channelSource ||
                    !isSubscribedToChannel(channelSource, entitlements));
            const isContentLocked =
                embedded &&
                needsToLockContent(
                    profileParentalControlRating,
                    videoItem?.parentalRating,
                );
            const isContentUnlocked =
                unlockedDetailId && unlockedDetailId === videoItem.id;

            if (prevParentalRatingChannelId.current !== videoItem.channelId) {
                prevParentalRating.current = 0;
                prevParentalRatingChannelId.current = videoItem.channelId;
            }

            const parentalRatingIsHigher =
                getParentalControlRatingNumbers(videoItem.parentalRating) >
                prevParentalRating.current;

            // Program related checks
            const requestPinEntry =
                (programOrChannelChanged || networkChanged) &&
                videoItem &&
                needsToShowPinDialog(
                    profileParentalControlRating,
                    videoItem?.parentalRating,
                ) &&
                parentalRatingIsHigher &&
                !isContentUnlocked &&
                checkCashedPinByRating(videoItem?.parentalRating);

            if (notSubscribed) {
                onUnsubscribedChannelError(videoSessionId.current);

                if (isPhone && !embedded && fullscreen) {
                    onBeforeShowPlayerModal();
                }
            } else if (isPlayerBlackout) {
                onSetPlayerError(
                    {
                        message: t(LANGUAGE_KEYS.PLAYER_BLACKOUT_ERROR),
                        code: ERROR_CODE_A201,
                    },
                    videoSessionId.current,
                );

                if (isPhone && !embedded && fullscreen) {
                    onBeforeShowPlayerModal();
                }
            } else if (isContentLocked) {
                onSetPlayerError(
                    {
                        message: t(PLAYER_LOCKED_CONTENT_OVERLAY_TITLE),
                        description: t(
                            PLAYER_LOCKED_CONTENT_OVERLAY_DESCRIPTION_RN,
                        ),
                        withIcon: false,
                    },
                    '',
                );

                if (isPhone && !embedded && fullscreen) {
                    onBeforeShowPlayerModal();
                }
            } else if (requestPinEntry) {
                if (isPhone && !embedded && fullscreen) {
                    onAfterHidePlayerModal();
                }

                if (isPhone && embedded && fullscreen) {
                    lockToDefaultOrientation();
                }

                onShowPinDialog();
            } else if (originalPlaybackType === StreamTypes.vod) {
                if (
                    isPhone &&
                    !embedded &&
                    fullscreen &&
                    !shouldWarnOnMobileData
                ) {
                    onBeforeShowPlayerModal();
                }

                if (!shouldWarnOnMobileData) {
                    onShowContentRatingOverlay();
                }
            } else {
                if (
                    isPhone &&
                    !embedded &&
                    fullscreen &&
                    !shouldWarnOnMobileData
                ) {
                    onBeforeShowPlayerModal();
                }

                if (!shouldWarnOnMobileData) {
                    handleCreateStreamSession();
                }
            }
        }

        prevOutOfHomeBlockStream.current = outOfHomeBlockStream;
        prevVideoItem.current = videoItem;
        prevPlaybackType.current = playbackType;
    }, [
        embedded,
        fullscreen,
        outOfHomeBlockStream,
        playbackType,
        videoItem,
        channelsById,
        profileParentalControlRating,
        onUnsubscribedChannelError,
        onResetPlayerError,
        onShowPinDialog,
        handleCreateStreamSession,
        shouldUpdateId,
        originalPlaybackType,
        unlockedDetailId,
        shouldWarnOnMobileData,
    ]);

    useEffect(() => {
        RegularStorage.getItem(StorageKeys.CASHED_PIN_TIMESTAMP).then(
            (storedValue) => {
                cashedTimestampsByRating.current = storedValue;
            },
        );
    }, [visible]);

    const updateCashedPinsTimestamp = useCallback(
        (parentalRating?: EventParentalControlRating) => {
            const hasParentalRating =
                parentalRating && !parentalRating.includes('adult');

            if (hasParentalRating && visible && selectedProfileId) {
                const timestamp = Date.now();

                dispatch(
                    setCashedPin(selectedProfileId, parentalRating, timestamp),
                );
            }
        },
        [cashedPins, visible, selectedProfileId],
    );

    /**
     * When PIN validation was successful - close PIN dialog and start streaming.
     */
    const handleParentalControlChecked = useCallback(() => {
        if (fullscreen) {
            setTimeout(() => {
                onBeforeShowPlayerModal();
            }, 150);
        }

        onParentalControlChecked();
        prevParentalRating.current = getParentalControlRatingNumbers(
            originalPlaybackType === StreamTypes.vod
                ? undefined
                : videoItem?.parentalRating,
        );

        if (originalPlaybackType === StreamTypes.vod) {
            onShowContentRatingOverlay();
        } else {
            handleCreateStreamSession();
            dispatch(setUnlockId(videoItem?.id));
            updateCashedPinsTimestamp(videoItem?.parentalRating);
        }
    }, [
        fullscreen,
        handleCreateStreamSession,
        originalPlaybackType,
        videoItem,
        onParentalControlChecked,
        onShowContentRatingOverlay,
        updateCashedPinsTimestamp,
    ]);

    const handleParentalCheckDismiss = useCallback(
        (shouldSetPlayerError?: boolean) => {
            onResetVideoItem();
            onParentalCheckDismiss(shouldSetPlayerError);
        },
        [onResetVideoItem, onParentalCheckDismiss],
    );

    return {
        videoSource,
        onStopPlaybackSession,
        onResetVideoSource: handleResetVideoSource,
        onResetVideoItem,
        showPinDialog,
        onParentalCheckDismiss: handleParentalCheckDismiss,
        onParentalControlChecked: handleParentalControlChecked,
        showContentRatingOverlay,
    };
}

export default useVideoSource;
